/**
 * TEMP-H-004: Execution Settings Page
 * 
 * Main settings page showing all stage configurations with role-based editing.
 * Provides overview of effective launch settings and per-stage overrides.
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Settings,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Info,
  Shield,
  Users,
  Activity,
  Filter,
  Grid,
  List,
  Search
} from 'lucide-react';
import type { StageName } from '@/lib/shared-types';
import { STAGE_NAMES } from '@/lib/type-guards';

// Legacy type alias
type StageType = StageName;
import { useStageSettings, useAllStageSettings, transformBasicStageData } from '@/hooks/useStageSettings';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { StageSettingsPanel } from '@/components/StageSettingsPanel';
import { ControlErrorBoundary } from '@/components/ControlErrorBoundary';
import { SettingsErrorBoundary } from '@/components/ErrorBoundary';

interface ExecutionSettingsPageProps {
  className?: string;
}

interface StageFilterState {
  showAll: boolean;
  showModified: boolean;
  showEditable: boolean;
  searchQuery: string;
}

// Use shared stage names constant for consistency
const PIPELINE_STAGES: StageType[] = [...STAGE_NAMES];

export function ExecutionSettingsPage({ className = '' }: ExecutionSettingsPageProps) {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const { user: _user, isAdmin, isAuthenticated } = useServerAuth();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedStage] = useState<StageType | null>(null);
  const [filters, setFilters] = useState<StageFilterState>({
    showAll: true,
    showModified: false,
    showEditable: false,
    searchQuery: ''
  });

  // Fetch all stage settings
  const { 
    data: allStageSettings, 
    isLoading: isLoadingAll,
    error: allStagesError,
    refetch: refetchAll
  } = useAllStageSettings(executionId || '');

  // Individual stage settings (when selected)
  const {
    settings: _selectedStageSettings,
    isLoading: _isLoadingStage,
    error: _stageError,
    permissions: _permissions,
    hasOverrides: _hasOverrides,
    refetch: refetchStage
  } = useStageSettings(
    executionId || '',
    selectedStage || 'script'
  );

  // Process and filter stages
  const processedStages = useMemo(() => {
    if (!allStageSettings) return [];

    return PIPELINE_STAGES.map(stage => {
      const backendStageData = allStageSettings[stage];
      if (!backendStageData) {
        return {
          stage,
          settings: null,
          hasSettings: false,
          isModified: false,
          isEditable: false,
          activePersona: 'None'
        };
      }

      // Transform backend data to StageSettingsConfig
      const settings = transformBasicStageData(executionId || '', backendStageData);
      
      return {
        stage,
        settings,
        hasSettings: true,
        isModified: settings.overriddenFromLaunch,
        isEditable: settings.isEditable,
        activePersona: settings.activePersona.personaName
      };
    }).filter(stageData => {
      // Apply filters
      if (!filters.showAll && !stageData.hasSettings) return false;
      if (filters.showModified && !stageData.isModified) return false;
      if (filters.showEditable && !stageData.isEditable) return false;
      
      // Search filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          stageData.stage.toLowerCase().includes(query) ||
          stageData.activePersona.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  }, [allStageSettings, filters]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!allStageSettings) return { total: 0, modified: 0, editable: 0 };

    const stages = Object.values(allStageSettings);
    return {
      total: stages.length,
      modified: stages.filter(s => s.overriddenFromLaunch).length,
      editable: stages.filter(s => s.isEditable).length
    };
  }, [allStageSettings]);

  // TODO: Implement stage selection functionality
  // const handleStageClick = (_stage: StageType) => {
  //   console.log('Stage click handling not yet implemented');
  // };

  const handleRefresh = () => {
    refetchAll();
    if (selectedStage) {
      refetchStage();
    }
  };

  const updateFilters = (updates: Partial<StageFilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Authentication Required</h2>
          <p className="text-gray-400">You must be logged in to view execution settings.</p>
        </div>
      </div>
    );
  }

  if (!executionId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Invalid Execution</h2>
          <p className="text-gray-400">No execution ID provided in the URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-900 text-white ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </button>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Execution Settings</h1>
                  <p className="text-sm text-gray-400">
                    {executionId?.slice(0, 8).toUpperCase()} • {isAdmin ? 'Admin Access' : 'User Access'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Summary Stats */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Activity className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">{summaryStats.total}</span>
                  <span className="text-gray-500">stages</span>
                </div>
                <div className="flex items-center gap-1">
                  <Info className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400">{summaryStats.modified}</span>
                  <span className="text-gray-500">modified</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-400">{summaryStats.editable}</span>
                  <span className="text-gray-500">editable</span>
                </div>
              </div>

              {/* View Controls */}
              <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={handleRefresh}
                disabled={isLoadingAll}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingAll ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-4 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400">Filter:</span>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.showModified}
                onChange={(e) => updateFilters({ showModified: e.target.checked })}
                className="w-3 h-3 rounded border-gray-600 bg-gray-800 text-purple-500"
              />
              <span className="text-gray-300">Modified only</span>
            </label>
            
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.showEditable}
                onChange={(e) => updateFilters({ showEditable: e.target.checked })}
                className="w-3 h-3 rounded border-gray-600 bg-gray-800 text-purple-500"
              />
              <span className="text-gray-300">Editable only</span>
            </label>
          </div>

          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search stages or personas..."
                value={filters.searchQuery}
                onChange={(e) => updateFilters({ searchQuery: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <SettingsErrorBoundary>
          <ControlErrorBoundary>
          {allStagesError ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-300 mb-1">
                    Failed to Load Settings
                  </h3>
                  <p className="text-xs text-red-400 mb-3">
                    {allStagesError.message || 'Unable to fetch execution settings'}
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-600/50 rounded-md transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : isLoadingAll ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 animate-spin text-purple-400" />
                <span className="text-gray-400">Loading execution settings...</span>
              </div>
            </div>
          ) : processedStages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-500/10 flex items-center justify-center">
                <Settings className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No Stages Found</h3>
              <p className="text-gray-400 mb-4">
                {filters.showModified || filters.showEditable || filters.searchQuery
                  ? 'No stages match your current filters.'
                  : 'No stage settings are available for this execution.'}
              </p>
              {(filters.showModified || filters.showEditable || filters.searchQuery) && (
                <button
                  onClick={() => setFilters({ showAll: true, showModified: false, showEditable: false, searchQuery: '' })}
                  className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-6'
            }>
              {processedStages.map(({ stage, settings }) => (
                <div key={stage} className={viewMode === 'list' ? 'max-w-2xl' : ''}>
                  {settings ? (
                    <StageSettingsPanel
                      settings={settings}
                      onRefresh={handleRefresh}
                      showCompactView={viewMode === 'grid'}
                    />
                  ) : (
                    <div className="bg-gray-900/50 rounded-lg border border-gray-700 p-6">
                      <div className="text-center py-8">
                        <div className="text-gray-400">
                          <div className="text-lg font-medium capitalize">{stage} Settings</div>
                          <div className="text-sm mt-2">No settings configured for this stage</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </ControlErrorBoundary>
        </SettingsErrorBoundary>
      </div>
    </div>
  );
}

// Default export for compatibility
export default ExecutionSettingsPage;